import React, { useRef } from "react";
import { Box, CircularProgress, Typography, Button, Select, MenuItem } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import logo from "../../assets/icons/logo.png";
import { UpdateOrderStatus } from "../../api/order/OrderController";
import { toast } from "react-toastify";

const ProductViewDetailModal = ({ orderData, onStatusUpdated }) => {
  const printRef = useRef(); // Ref for printable content
  const [updatingStatus, setUpdatingStatus] = React.useState(false);

  const isLoading =
    !orderData ||
    !orderData.lstOrderDetails ||
    orderData.lstOrderDetails.length === 0;

  // Ensure lstOrderDetails is defined and has at least one item
  const firstOrder = orderData?.lstOrderDetails?.[0] || {};

  // Calculate Total Amount Before Discount
  const baseURL = process.env.REACT_APP_RESOURCE_ENDPOINT;
  const totalAmount =
    orderData?.lstOrderDetails?.reduce((sum, order) => sum + order.amount, 0) ||
    0;

  // Promo & Discount Details
  const promoCode = firstOrder.promoCode || "N/A";
  const paymentMethod = firstOrder.paymentMethod;
  const transactionPhoto = firstOrder.transactionPhoto;
  const businessName = firstOrder.businessName;
  const shouldShowTransactionPhoto =
    paymentMethod && paymentMethod !== "COD" && transactionPhoto && businessName;
  const discountPercentage = firstOrder.discountAmount || 0;
  const discountAmount = (totalAmount * discountPercentage) / 100;
  const finalTotalAmount = totalAmount - discountAmount;
  const currentStatus = firstOrder.orderStatus || "New";
  const [selectedStatus, setSelectedStatus] = React.useState(currentStatus);

  React.useEffect(() => {
    setSelectedStatus(currentStatus);
  }, [currentStatus]);

  // Print Function
  const handlePrint = () => {
    const printWindow = window.open("", "", "width=600,height=800");

    const contactNumbers = [
      "၀၁ ၇၆၄၀ ၄၈၀",
      "၀၁ ၇၆၄၀ ၄၈၁",
      "၀၁ ၇၆၄၀ ၂၂၀",
      "၀၁ ၈၆၅၅ ၁၂၅",
      "၀၁ ၈၆၅၅ ၁၃၅",
      "၀၉ ၉၇၇ ၂၅၇ ၂၂၈",
      "၀၉ ၉၇၇ ၂၅၇ ၈၆၀",
      "၀၉ ၉၆၅ ၁၁၀ ၄၃၀",
      "၀၉ ၆၈ ၁၁၁၁ ၄၁၂",
      "၀၉ ၆၈ ၁၁၁၁ ၄၁၃",
    ];
    const contactEmail = "mandalaywph258384@gmail.com";
    const contactAddress =
      "မန္တလာ သံဆိုင်၊ အဆောင် ၉၄၊ အခန်း ၁၅/၁၆၊ ပုလဲလမ်း၊ စော်ဘွားကြီးကုန်း ပွဲရုံဝင်း၊ အင်းစိန် မြို့နယ်၊ ရန်ကုန်တိုင်း ဒေသကြီး";

    printWindow.document.write(`
      <html>
      <head>
        <title>Mandalay HW-Order Receipt</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          .header { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #eee; padding-bottom: 10px; margin-bottom: 15px; }
          .logo { width: 60px; height: auto; object-fit: contain; }
          .title { text-align: center; font-size: 24px; font-weight: bold; color: #3F51B5; }
          .section { margin-top: 15px; font-size: 16px; }
          .bold { font-weight: bold; color: #3F51B5; }
          .total { font-weight: bold; color: #28A745; font-size: 20px; text-align: center; margin-top: 15px; }
          .divider { border-bottom: 2px solid #ddd; margin: 15px 0; }
          .center { text-align: center; }
          .right { text-align: right; }
          .discount { color: #D32F2F; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th, td { border: 1px solid #E0E0E0; padding: 6px 8px; font-size: 13px; text-align: left; }
          th { background: #F5F7FB; color: #3F51B5; font-weight: bold; }
          .num { text-align: right; white-space: nowrap; }
          .contact { margin-top: 15px; font-size: 13px; color: #555; }
          .contact-title { font-weight: bold; color: #3F51B5; margin-bottom: 6px; }
          .thanks { margin-top: 12px; text-align: center; font-weight: bold; color: #3F51B5; }
        </style>
      </head>
      <body>
        <div class="header">
          <img class="logo" src="${logo}" alt="Mandalay HW logo" />
          <div>
            <div style="font-size:18px;font-weight:bold;color:#3F51B5;">Mandalay HW</div>
            <div style="font-size:12px;color:#666;">Order Receipt</div>
          </div>
        </div>
        
        <div class="section">
          <div class="bold">Order ID:</div>
          <div>${firstOrder.order_Id}</div>
        </div>

        <div class="divider"></div>

        <div class="section">
          <div class="bold">Customer Info:</div>
          <div><strong>Name:</strong> ${firstOrder.fullName}</div>
          <div><strong>Address:</strong> ${firstOrder.address}, ${firstOrder.shippingTownship}</div>
        </div>

        <div class="divider"></div>

        <div class="section">
          <div class="bold">Products Ordered:</div>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Code</th>
                <th>Unit</th>
                <th class="num">Qty</th>
                <th class="num">Unit Price</th>
                <th class="num">Total</th>
              </tr>
            </thead>
            <tbody>
              ${orderData.lstOrderDetails
                .map((order) => {
                  const unitValue =
                    order.unit ||
                    order.Unit ||
                    order.unitTitle ||
                    order.UnitTitle ||
                    order.unitName ||
                    order.UnitName ||
                    order.size;
                  const displayUnit =
                    typeof unitValue === "string" ? unitValue.trim() : unitValue;
                  const unitPrice = Number(order.unitPrice || 0);
                  const quantity = Number(order.quantity || 0);
                  const lineTotal = unitPrice * quantity;
                  return `
                    <tr>
                      <td>${order.product_Name || "-"}</td>
                      <td>${order.productCode || "-"}</td>
                      <td>${displayUnit || "-"}</td>
                      <td class="num">${quantity}</td>
                      <td class="num">${new Intl.NumberFormat("en-US").format(unitPrice)} MMK</td>
                      <td class="num">${new Intl.NumberFormat("en-US").format(lineTotal)} MMK</td>
                    </tr>
                  `;
                })
                .join("")}
            </tbody>
          </table>
        </div>

        <div class="divider"></div>

        <div class="section total">
          <div class="bold">Total Amount:</div>
          <div>MMK ${new Intl.NumberFormat("en-US").format(totalAmount)}</div>
        </div>

        <div class="divider"></div>

        <div class="section center">
          <div class="bold">Promo Code Applied:</div>
          <div style="color: #FFA500; font-weight: bold;">${promoCode}</div>
          <div class="discount">Discount: ${discountPercentage}% (-${new Intl.NumberFormat("en-US").format(
      discountAmount
    )} MMK)</div>
        </div>

        <div class="divider"></div>

        <div class="section total">
          <div class="bold">Final Total Amount:</div>
          <div>MMK ${new Intl.NumberFormat("en-US").format(finalTotalAmount)}</div>
        </div>

        <div class="divider"></div>

        <div class="contact">
          <div class="contact-title">Contact Us</div>
          <div><strong>Address:</strong> ${contactAddress}</div>
          <div><strong>Phone:</strong> ${contactNumbers.join(", ")}</div>
          <div><strong>Email:</strong> ${contactEmail}</div>
        </div>

        <div class="thanks">Thank you for your order!</div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };
  return (
    <div
      style={{
        width: "100%",
        padding: "20px",
        height: "500px",
        maxHeight: "500px",
        overflowY: "auto",
        scrollbarWidth: "none",
      }}
    >
      <div ref={printRef}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: "#3F51B5",
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          Order Details
        </Typography>

        {isLoading && (
          <div
            style={{
              width: "100%",
              height: "350px",
              padding: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </div>
        )}

        {!isLoading && (
          <>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "bold",
                color: "#3F51B5",
                marginBottom: "10px",
                textAlign: "left",
              }}
            >
              Order ID:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#5A5A5A",
                marginBottom: "20px",
                fontSize: "16px",
                wordBreak: "break-word",
                textAlign: "left",
              }}
            >
              {orderData.lstOrderDetails[0].order_Id}
            </Typography>

            {/* Full Name & Address */}
            <div style={{ marginBottom: "15px" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#3F51B5", display: "inline" }}
              >
                Full Name:{" "}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#5A5A5A", display: "inline" }}
              >
                {orderData.lstOrderDetails[0].fullName}
              </Typography>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#3F51B5", display: "inline" }}
              >
                Phone Number:{" "}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#5A5A5A", display: "inline" }}
              >
                {orderData.lstOrderDetails[0].phone || "-"}
              </Typography>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#3F51B5", display: "inline" }}
              >
                Address:{" "}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#5A5A5A", display: "inline" }}
              >
                {orderData.lstOrderDetails[0].address},{" "}
                {orderData.lstOrderDetails[0].shippingTownship}
              </Typography>
            </div>

            {/* Status & Payment Method */}
            <div style={{ marginBottom: "15px" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#3F51B5", display: "inline" }}
              >
                Status:{" "}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "bold",
                  color:
                    orderData.lstOrderDetails[0].orderStatus === "Completed"
                      ? "#28A745"
                      : "#FF6F61",
                  display: "inline",
                }}
              >
                {orderData.lstOrderDetails[0].orderStatus}
              </Typography>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#3F51B5", marginBottom: "6px" }}
              >
                Change Status
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Select
                  size="small"
                  value={selectedStatus}
                  onChange={(event) => setSelectedStatus(event.target.value)}
                  sx={{ minWidth: 190 }}
                >
                  <MenuItem value="Accepted">Accepted</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="Returned">Returned</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
                <Button
                  variant="contained"
                  disabled={
                    updatingStatus ||
                    !orderData?.lstOrderDetails?.[0]?.order_Id ||
                    selectedStatus === currentStatus
                  }
                  onClick={async () => {
                    const orderId = orderData?.lstOrderDetails?.[0]?.order_Id;
                    if (!orderId) {
                      return;
                    }
                    const response = await UpdateOrderStatus(orderId, selectedStatus, setUpdatingStatus);
                    if (response?.baseResponseModel?.respCode === 200) {
                      toast.success("Order status updated.");
                      if (typeof onStatusUpdated === "function") {
                        await onStatusUpdated();
                      }
                    } else {
                      toast.error(response?.baseResponseModel?.respMessage || "Failed to update status.");
                    }
                  }}
                >
                  {updatingStatus ? "Updating..." : "Update Status"}
                </Button>
              </Box>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "#3F51B5", display: "inline" }}
              >
                Payment Method:{" "}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#5A5A5A", display: "inline" }}
              >
                {paymentMethod}
              </Typography>
            </div>
            {shouldShowTransactionPhoto && (
              <div style={{ marginBottom: "20px" }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", color: "#3F51B5", marginBottom: "8px" }}
                >
                  Payment Screenshot:
                </Typography>
                <img
                  src={`${baseURL}Upload/Ecommerce/${businessName}/Transaction/${transactionPhoto}`}
                  alt="Payment screenshot"
                  style={{
                    width: "100%",
                    maxWidth: "320px",
                    borderRadius: "8px",
                    border: "1px solid #E0E0E0",
                  }}
                />
              </div>
            )}

            {/* Product Details */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: "#3F51B5",
                marginTop: "20px",
                marginBottom: "10px",
                textAlign: "left",
              }}
            >
              Product Details (Unit & Qty):
            </Typography>
            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr 0.8fr 1fr 1fr",
                  gap: "8px",
                  backgroundColor: "#F5F7FB",
                  padding: "10px 12px",
                  fontWeight: 700,
                  color: "#3F51B5",
                  fontSize: "13px",
                }}
              >
                <span>Product</span>
                <span>Code</span>
                <span>Unit</span>
                <span>Qty</span>
                <span>Unit Price</span>
                <span>Total</span>
              </Box>
              {orderData.lstOrderDetails.map((order) => {
                const unitPrice = Number(order.unitPrice || 0);
                const quantity = Number(order.quantity || 0);
                const lineTotal = unitPrice * quantity;
                const unitValue =
                  order.unit ||
                  order.Unit ||
                  order.unitTitle ||
                  order.UnitTitle ||
                  order.unitName ||
                  order.UnitName ||
                  order.size;
                const displayUnit =
                  typeof unitValue === "string" ? unitValue.trim() : unitValue;
                return (
                  <Box
                    key={order.id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 0.8fr 1fr 1fr",
                      gap: "8px",
                      padding: "10px 12px",
                      borderTop: "1px solid #EFEFEF",
                      color: "#5A5A5A",
                      fontSize: "13px",
                    }}
                  >
                    <span>{order.product_Name}</span>
                    <span>{order.productCode || "-"}</span>
                    <span>{displayUnit || "-"}</span>
                    <span>{quantity}</span>
                    <span>
                      {new Intl.NumberFormat("en-US").format(unitPrice)} MMK
                    </span>
                    <span>
                      {new Intl.NumberFormat("en-US").format(lineTotal)} MMK
                    </span>
                  </Box>
                );
              })}
            </Box>

            {/* Total Amount */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#3F51B5",
                marginTop: "20px",
                textAlign: "center",
              }}
            >
              Total Amount:
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#28A745",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              MMK {new Intl.NumberFormat("en-US").format(totalAmount)}
            </Typography>

            {/* Promo Code & Discount */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#3F51B5",
                marginTop: "20px",
                textAlign: "center",
              }}
            >
              Promo Code Applied:
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#FFA500",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              {promoCode}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#5A5A5A",
                textAlign: "center",
                marginTop: "10px",
              }}
            >
              Discount:{" "}
              <span style={{ fontWeight: "bold", color: "#D32F2F" }}>
                {discountPercentage}% (-
                {new Intl.NumberFormat("en-US").format(discountAmount)} MMK)
              </span>
            </Typography>

            {/* Final Total Amount */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#3F51B5",
                marginTop: "20px",
                textAlign: "center",
              }}
            >
              Final Total Amount:
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: "#28A745",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              MMK {new Intl.NumberFormat("en-US").format(finalTotalAmount)}
            </Typography>
          </>
        )}
      </div>

      {/* Print Button */}
      {!isLoading && (
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
          startIcon={<PrintIcon />}
          sx={{
            marginTop: "15px",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Print Receipt
        </Button>
      )}
    </div>
  );
};

export default ProductViewDetailModal;
